import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Button from '@welldev-group/rcl-button';
import Input from '@welldev-group/rcl-input';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { GET_CAR_RENTAL_COMPANY } from '../../graphql/queries/carRentalCompany';

import {
  CREATE_CAR_RENTAL_COMPANY,
  EDIT_CAR_RENTAL_COMPANY,
} from '../../graphql/mutations/carRentalCompany';

import { useTranslation } from 'react-i18next';
import imageUrl from '../../helpers/imageUrl';
import clearCache from '../../helpers/clearCache';
import FileUpload from '../common/FileUpload';
import ErrorComponent from '../common/ErrorComponent';
import WithErrorHandler from '../common/WithErrorHandler';
import { getCarRentalCompany } from '../../graphql/queries/carRentalCompany';
import { debounce } from 'lodash';

const CarRentalCompanyForm = props => {
  const { t } = useTranslation();
  let { id } = useParams();
  const isNewRecord = !id;

  const [createCarRentalCompany, callbackDataOnCreate] = useMutation(CREATE_CAR_RENTAL_COMPANY, {
    update: cache => clearCache(cache, /CarRentalCompany/),
  });
  const [editCarRentalCompany, callbackDataOnEdit] = useMutation(EDIT_CAR_RENTAL_COMPANY);
  const carRentalCompanyFromId = useQuery(GET_CAR_RENTAL_COMPANY, {
    variables: { id },
    skip: isNewRecord,
  });

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [image, setImage] = useState(null);
  const [previousImage, setPreviousImage] = useState(null);

  const onNameChange = value => {
    setName(value);
  };

  const onCodeChange = value => {
    setCode(value);
  };
  const onImageFileUpload = files => {
    setImage(files[0]);
  };

  useEffect(() => {
    if (carRentalCompanyFromId && carRentalCompanyFromId.data && !carRentalCompanyFromId.loading) {
      setName(carRentalCompanyFromId.data.carRentalCompany.name);
      setCode(carRentalCompanyFromId.data.carRentalCompany.code);
      setPreviousImage(carRentalCompanyFromId.data.carRentalCompany.imageUrls.large);
    }
  }, [carRentalCompanyFromId]);

  const submitToBackEnd = debounce(() => {
    props.resetError();

    if (isNewRecord) {
      createCarRentalCompany({
        variables: {
          name,
          code,
          image,
        },
      })
        .then(({ data }) => {
          props.history.push(`/admin/car_rental_companies/${data.createCarRentalCompany.id}`);
        })
        .catch(() => props.onError());
    } else {
      editCarRentalCompany({
        variables: {
          id,
          name,
          code,
          image,
        },
        refetchQueries: [getCarRentalCompany(id)],
      })
        .then(({ data }) => {
          props.history.push(`/admin/car_rental_companies/${data.editCarRentalCompany.id}`);
        })
        .catch(() => props.onError());
    }
  }, 500);

  return (
    <div className="common-form">
      <div className="common-form__title">
        {isNewRecord ?
          t('shared.new', { entityName: t('carRentalCompanies.show.title') })
          : t('shared.editTitle', { entityName: t('carRentalCompanies.show.title') })}
      </div>
      <div className="common-form__error-message">
        {props.hasError && (
          <div className="col-6">
            <ErrorComponent error={{ callbackDataOnEdit, callbackDataOnCreate }} />
          </div>
        )}
      </div>
      <form className="common-form__container">
        <div className="grid">
          <div className="col-lg-6 col-md-10 col-sm-10 col-xs-10">
            <div className="col-lg-12 col-md-6 col-sm-6 col-xs-12 col-bleed-y">
              <div className="common-form__field car-rental-company-form__name">
                <Input
                  label={t('carRentalCompanies.attributes.name')}
                  value={name}
                  onChange={onNameChange}
                  isClearable={false}
                />
              </div>
              <div className="common-form__field car-rental-company-form__code">
                <Input
                  label={t('carRentalCompanies.attributes.code')}
                  value={code}
                  onChange={onCodeChange}
                  isClearable={false}
                />
              </div>
              <div className="common-form__submit-button">
                <Button
                  label={isNewRecord ? t('shared.create') : t('shared.update')}
                  onClick={submitToBackEnd}
                />
              </div>
            </div>
          </div>
          <div className="col-6 col-bleed-y">
            <h2>{t('carRentalCompanies.attributes.image')}</h2>
            {previousImage && (
              <div className="common-form__previous-image">
                <img src={imageUrl(previousImage)} alt="preview" />
              </div>
            )}
            <FileUpload onFileUpload={onImageFileUpload} multiple={false} />
          </div>
        </div>
      </form>
    </div>
  );
};

export default WithErrorHandler(CarRentalCompanyForm);
